import React, { useState } from 'react';
import { ImageOff } from 'lucide-react';

import { cn } from '@/lib/utils';
import { MediaLightbox } from './MediaLightbox';

export interface ProductImageTileProps {
    src?: string | null;
    /** The product's name: alt text, and the lightbox's title. */
    alt?: string;
    /** Square edge in px. Leave out with `fluid` to fill the parent's width. */
    size?: number;
    /** A square as wide as its container (a grid card). */
    fluid?: boolean;
    /** Click opens the picture large (default: on whenever there is a picture). */
    zoomable?: boolean;
    /** Said in place of the picture when there is none, or it does not load. */
    emptyLabel?: string;
    /** Label of the click-to-enlarge button (screen readers, tooltip). */
    openLabel?: string;
    className?: string;
    testId?: string;
}

/**
 * A product's picture as a square tile: cropped to fill (object-fit cover),
 * hairline frame, and a click that opens it large — big enough to judge a
 * surface by. When there is no picture, or it fails to load, a calm frame
 * says so instead of a broken image.
 *
 * One tile for every place products are compared or listed (the network review
 * queue, a brand's products, a product's media, a promote picker), so the same
 * product looks the same everywhere.
 */
export const ProductImageTile: React.FC<ProductImageTileProps> = ({
    src, alt = '', size = 64, fluid, zoomable, emptyLabel, openLabel, className, testId,
}) => {
    const [open, setOpen] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);
    const usable = !!src && failed !== src;
    const canZoom = usable && zoomable !== false;
    const style: React.CSSProperties | undefined = fluid ? undefined : { width: size, height: size };
    const frame = cn(
        'relative shrink-0 overflow-hidden rounded-lg border bg-muted/40',
        fluid && 'aspect-square w-full',
        className,
    );

    if (!usable) {
        return (
            <div
                className={cn(frame, 'flex flex-col items-center justify-center gap-1 px-2 text-center text-muted-foreground')}
                style={style}
                role="img"
                aria-label={emptyLabel || alt}
                title={emptyLabel}
                data-testid={testId}
            >
                <ImageOff className={fluid || size >= 96 ? 'size-5' : 'size-3.5'} aria-hidden />
                {emptyLabel && (fluid || size >= 120) && <span className="text-xs">{emptyLabel}</span>}
            </div>
        );
    }

    const img = (
        <img
            src={src}
            alt={alt}
            loading="lazy"
            onError={() => setFailed(src)}
            className="size-full object-cover"
        />
    );

    return (
        <>
            {canZoom ? (
                <button
                    type="button"
                    className={cn(frame, 'cursor-zoom-in p-0 transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none')}
                    style={style}
                    onClick={(e) => {
                        // Tiles sit inside clickable rows and cards: enlarging is not opening.
                        e.stopPropagation();
                        setOpen(true);
                    }}
                    aria-label={openLabel ? `${openLabel}${alt ? ` — ${alt}` : ''}` : alt}
                    title={openLabel}
                    data-testid={testId}
                >
                    {img}
                </button>
            ) : (
                <div className={frame} style={style} data-testid={testId}>
                    {img}
                </div>
            )}
            {canZoom && <MediaLightbox media={open ? { url: src as string, kind: 'image', title: alt } : null} onClose={() => setOpen(false)} />}
        </>
    );
};
